<template>
  <sub-page :title="$t('payschedules.table.title')" :subtitle="$t('payschedules.table.subtitle')">
    <template v-slot:header>
      <b-button v-if="!isDisablePayscheduleEditing" @click="goToCreatePaySchedule" variant="outline-primary">
        <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />{{ $t('payschedules.table.createButton') }}
      </b-button>
    </template>

    <sme-alert v-if="errorMessage" :html="errorMessage" level="danger" class="my-3" />

    <header class="d-flex flex-column flex-sm-row align-items-end mb-3">
      <sme-alert v-if="isAnyPayPeriodStartWithin7Days" :level="ALERT_LEVELS.INFO" class="mb-3 mb-sm-0">
        {{ $t('payschedules.table.upcomingReviewAlert') }}
      </sme-alert>
    </header>

    <sme-detailed-table
      :fields="FIELDS.value || FIELDS"
      :items="payschedules"
      :loading="loading"
      tbody-tr-class="sme-detailed-table__row--link"
      @row-clicked="item => $router.push({ name: 'pay-schedule', params: { payScheduleId: item.pay_schedule_id } })"
    >
      <template #empty>
        <sme-alert level="warning">
          <strong>{{ $t('payschedules.table.noSchedulesFound') }}</strong>
          <template v-if="!isDisablePayscheduleEditing">{{ $t('payschedules.table.createOneBody') }}</template>
        </sme-alert>
        <b-button v-if="!isDisablePayscheduleEditing" @click="goToCreatePaySchedule" class="mt-3" variant="primary">
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />{{ $t('payschedules.table.createButton') }}
        </b-button>
      </template>
      <template #cell(name)="data">
        <div class="sme-detailed-table__cell sme-detailed-table__cell--wide">
          <strong class="sme-detailed-table__label sme-detailed-table__label--large">
            {{ data.item.name }}
          </strong>
          <div>
            <sme-badge
              :to="{ name: 'employees', query: { [EMPLOYEE_FILTER_KEYS.PAY_SCHEDULE]: data.item.pay_schedule_id } }"
              class="sme-detailed-table__badge"
              pill
            >
              {{ getEmployeeCountText(data.item.pay_schedule_id) }}
              <font-awesome-icon :icon="['fad', 'external-link']" class="ml-1" />
            </sme-badge>
          </div>
        </div>
      </template>
    </sme-detailed-table>
  </sub-page>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import SubPage from '@/components/SubPage.vue';
import SmeAlert, { ALERT_LEVELS } from '@/components/atoms/SmeAlert';
import SmeBadge from '@/components/atoms/SmeBadge.vue';
import SmeDetailedTable from '@/components/atoms/SmeDetailedTable.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import i18n from '@/i18n';
import { FILTER_KEYS as EMPLOYEE_FILTER_KEYS } from '@/pages/employees/components/EmployeesList.vue';
import usePayschedules from '@/state/composables/usePayschedules';
import { toTitleCase, getPayTypeLabel } from '@/utils/common';
import { toFormattedDate } from '@/utils/date';
import isAnyPayPeriodStartWithin7Days from '@/pages/dashboard/components/todos/DashboardTodos.vue';
import useAnalytics from '@/state/composables/useAnalytics';

const { postAnalyticsEvent } = useAnalytics();
const $router = useRouter();
const { getPayschedules, loading: payschedulesLoading, payschedules } = usePayschedules();
const { isDisablePayscheduleEditing, isUseUserCountInsteadOfEmployeesCount } = useFeatureFlags();

// computed so that i18n.t() runs on every locale switch
const FIELDS = computed(() => [
  {
    key: 'name',
    label: i18n.t('payschedules.table.nameHeader'),
  },
  {
    key: 'accrual_type',
    label: i18n.t('payschedules.table.accrualTypeHeader'),
    formatter: value => toTitleCase(value),
  },
  {
    key: 'pay_period_start',
    label: i18n.t('payschedules.table.payPeriodStartHeader'),
    formatter: value => toFormattedDate(value),
  },
  {
    key: 'blackout_period_start',
    label: i18n.t('payschedules.table.blackoutStartHeader'),
    formatter: value => (value ? toFormattedDate(value) : i18n.t('payschedules.table.noneValue')),
  },
  {
    key: 'next_payday',
    label: i18n.t('payschedules.table.nextPaydayHeader'),
    formatter: value => toFormattedDate(value),
  },
  {
    key: 'pay_schedule_code',
    label: i18n.t('payschedules.table.payTypeHeader'),
    formatter: value => getPayTypeLabel(value),
  },
]);

const errorMessage = ref('');

const loading = computed(() => payschedulesLoading.value);

const employeePayscheduleCounts = computed(() => {
  const payscheduleCounts = {};
  payschedules.value.forEach(ps => {
    payscheduleCounts[ps.pay_schedule_id] = ps.employee_count;
  });
  return payscheduleCounts;
});

const getEmployeeCountText = payscheduleId => {
  if (isUseUserCountInsteadOfEmployeesCount.value) {
    return i18n.t('payschedules.table.viewEmployeesBadge');
  }

  const numEmployees = employeePayscheduleCounts.value[payscheduleId];
  return numEmployees === 1
    ? i18n.t('payschedules.table.employeeCountSingular', { count: numEmployees })
    : i18n.t('payschedules.table.employeeCountPlural', { count: numEmployees });
};

const goToCreatePaySchedule = () => {
  postAnalyticsEvent({
    intent: 'navigate_pay_schedules_add_pay_schedule',
    task: 'add_pay_schedule',
    action: 'click',
  });
  $router.push({ name: 'pay-schedule', params: { payScheduleId: 'create' } });
};

getPayschedules();
</script>
